import { FormEventHandler, useCallback, useEffect, useRef, useState } from "react";
import {
  Badge,
  Box,
  Button,
  Checkbox,
  Flex,
  Heading,
  Text,
  IconButton,
} from "@chakra-ui/react";
import { MdArrowBack } from "react-icons/md";
import { DeviceOrientationAlert } from "@/components";
import { DeviceOrientationEventiOS } from "@/types/data";
import { Modal } from "@/components/ui/modal";

type ImageWarning = "too_dark" | "too_bright" | "low_contrast";

const WARNING_LABELS: Record<ImageWarning, string> = {
  too_dark: "Too dark",
  too_bright: "Too bright",
  low_contrast: "Low contrast",
};
const GUIDE_HIDDEN_KEY = "rack_photo_guide_hidden";

function analyzeFrame(video: HTMLVideoElement): ImageWarning[] {
  const W = 160,
    H = 90;
  const canvas = document.createElement("canvas");
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext("2d")!;
  ctx.drawImage(video, 0, 0, W, H);
  const { data } = ctx.getImageData(0, 0, W, H);

  const lums: number[] = [];
  for (let i = 0; i < data.length; i += 4) {
    lums.push(0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2]);
  }

  const avg = lums.reduce((s, v) => s + v, 0) / lums.length;
  const stdDev = Math.sqrt(
    lums.reduce((s, v) => s + (v - avg) ** 2, 0) / lums.length,
  );

  const warnings: ImageWarning[] = [];
  if (avg < 50) warnings.push("too_dark");
  else if (avg > 180) warnings.push("too_bright");
  if (stdDev < 25) warnings.push("low_contrast");
  return warnings;
}

interface ILiveCamera {
  onCapture: (file: File) => void;
  onBack: () => void;
  onUpload: (file: File) => void;
  orientation: DeviceOrientationEventiOS | null;
  requestOrientationPermission: boolean;
  orientationPermissionCallback: () => Promise<void>;
  countMode?: boolean;
}

function LiveCamera({
  onCapture,
  onBack,
  onUpload,
  orientation,
  requestOrientationPermission,
  orientationPermissionCallback,
  countMode = false,
}: ILiveCamera) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [showLiveGuide, setShowLiveGuide] = useState(true);
  const [orientationModalDismissed, setOrientationModalDismissed] =
    useState(false);
  const [warnings, setWarnings] = useState<ImageWarning[]>([]);

  const [photoGuideHidden, setPhotoGuideHidden] = useState(
    () => localStorage.getItem(GUIDE_HIDDEN_KEY) === "true",
  );
  const [modalDismissed, setModalDismissed] = useState(photoGuideHidden);
  const setHidePhotoGuide = useCallback((v: boolean) => {
    setPhotoGuideHidden(v);
    if (v) {
      localStorage.setItem(GUIDE_HIDDEN_KEY, "true");
    } else {
      localStorage.removeItem(GUIDE_HIDDEN_KEY);
    }
  }, []);

  const showOrientationModal =
    modalDismissed &&
    requestOrientationPermission &&
    !orientationModalDismissed;

  useEffect(() => {
    let mounted = true;

    async function startCamera() {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: { ideal: "environment" },
            width: { ideal: 1920 },
            height: { ideal: 1080 },
          },
        });
        if (!mounted) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        if (videoRef.current) videoRef.current.srcObject = stream;
      } catch (err) {
        console.error("Camera error:", err);
      }
    }

    startCamera();

    return () => {
      mounted = false;
      streamRef.current?.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    };
  }, []);

  // Analyze brightness and contrast every 500ms
  useEffect(() => {
    const interval = setInterval(() => {
      const video = videoRef.current;
      if (!video || !video.videoWidth) return;
      setWarnings(analyzeFrame(video));
    }, 500);
    return () => clearInterval(interval);
  }, []);

  const capture = () => {
    const video = videoRef.current;
    if (!video || !video.videoWidth) return;

    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext("2d")!.drawImage(video, 0, 0);

    canvas.toBlob(
      (blob) => {
        if (!blob) return;
        onCapture(
          new File([blob], `${crypto.randomUUID()}/observation.jpg`, {
            type: "image/jpeg",
          }),
        );
      },
      "image/jpeg",
      0.95,
    );
  };

  const handleFileChange: FormEventHandler<HTMLInputElement> = (e) => {
    const file = (e.target as HTMLInputElement).files?.[0];
    if (!file) return;
    const ext = file.name.slice(file.name.lastIndexOf(".") + 1);
    onUpload(
      new File([file], `${crypto.randomUUID()}/observation.${ext}`, {
        type: file.type,
        lastModified: file.lastModified,
      }),
    );
  };

  return (
    <Box position="fixed" inset={0} bg="black" zIndex={50}>
      {/* Live video feed */}
      <video
        ref={videoRef}
        autoPlay
        playsInline
        muted
        style={{ width: "100%", height: "100%", objectFit: "cover" }}
      />

      {/* Viewfinder mask */}
      {showLiveGuide && (
        <Box
          position="absolute"
          inset={0}
          display="flex"
          alignItems="center"
          justifyContent="center"
          pointerEvents="none"
        >
          <Box
            w="85vmin"
            h="85vmin"
            borderRadius="lg"
            boxShadow="0 0 0 100vmax rgba(0,0,0,0.55)"
            position="relative"
          >
            {countMode && (
              <Box
                position="absolute"
                top="-7rem"
                left={0}
                right={0}
                textAlign="center"
                bg="bg.warning"
                borderColor="orange.muted"
                borderWidth={1}
                rounded="md"
                p={1}
              >
                <Text fontSize="sm" fontWeight={600}>
                  Rapid inventory count - not saved to rack
                </Text>
              </Box>
            )}
            <Text
              position="absolute"
              top="-4rem"
              left={0}
              right={0}
              textAlign="center"
              color="fg.inverted"
              fontSize="sm"
              px={4}
            >
              Ensure entire rack is captured and visible in the frame, with no
              reflections or wide angle distortion
            </Text>
          </Box>
        </Box>
      )}

      {/* Back button */}
      <Box position="absolute" top={4} left={4}>
        <IconButton
          aria-label="Back"
          onClick={onBack}
          variant="ghost"
          color="white"
          _hover={{ bg: "whiteAlpha.300" }}
          size="lg"
        >
          <MdArrowBack />
        </IconButton>
      </Box>

      {/* Image quality warnings */}
      <Flex
        position="absolute"
        bottom={36}
        left="50%"
        transform="translateX(-50%)"
        gap={2}
        whiteSpace="nowrap"
      >
        {warnings.map((w) => (
          <Badge key={w} variant="solid" colorPalette="red" size="sm">
            {WARNING_LABELS[w]}
          </Badge>
        ))}
        <DeviceOrientationAlert orientation={orientation} />
      </Flex>

      {/* Shutter button */}
      <Box
        position="absolute"
        bottom={12}
        left="50%"
        transform="translateX(-50%)"
      >
        <Box
          as="button"
          onClick={capture}
          aria-label="Take photo"
          w="64px"
          h="64px"
          borderRadius="full"
          bg="white"
          border="4px solid"
          borderColor="whiteAlpha.600"
          outline="1px solid white"
          outlineOffset="3px"
          _active={{ transform: "scale(0.93)" }}
          transition="transform 0.1s"
        />
      </Box>

      {/* Guide toggle */}
      <Box
        position="absolute"
        bottom={4}
        left="50%"
        transform="translateX(-50%)"
        whiteSpace="nowrap"
      >
        <Checkbox.Root
          checked={showLiveGuide}
          onCheckedChange={({ checked }) => setShowLiveGuide(!!checked)}
          size="sm"
        >
          <Checkbox.HiddenInput />
          <Checkbox.Control />
          <Checkbox.Label color="white" fontSize="xs">
            Show camera frame guide
          </Checkbox.Label>
        </Checkbox.Root>
      </Box>

      {showOrientationModal && (
        <Modal>
          <Text fontSize="sm" color="gray.600" lineHeight="tall">
            Coracle can analyze your device position to ensure the image is
            captured at a correct angle.
          </Text>
          <Text
            fontSize="sm"
            fontWeight="bold"
            color="gray.600"
            lineHeight="tall"
            alignSelf="start"
          >
            Would you like to use this resource?
          </Text>
          <Flex gap={2} w="full">
            <Button
              flex="1"
              variant="outline"
              size="sm"
              onClick={() => setOrientationModalDismissed(true)}
            >
              No
            </Button>
            <Button
              flex="1"
              colorPalette="blue"
              size="sm"
              onClick={async () => {
                await orientationPermissionCallback();
                setOrientationModalDismissed(true);
              }}
            >
              Yes
            </Button>
          </Flex>
        </Modal>
      )}

      {/* Onboarding modal */}
      {!modalDismissed && (
        <Modal>
          <Heading size="lg" color="gray.800" w="full">
            Coracle image capture
          </Heading>

          {/* Phone outline illustration */}
          <Box
            position="relative"
            w="80px"
            h="140px"
            border="2px solid"
            borderColor="gray.300"
            borderRadius="lg"
            flexShrink={0}
          >
            {/* Notch */}
            <Box
              position="absolute"
              top="4px"
              left="50%"
              transform="translateX(-50%)"
              w="25%"
              h="2px"
              bg="gray.400"
              borderRadius="full"
            />
            {/* Screen with guide SVG */}
            <Box
              position="absolute"
              top="20%"
              bottom="20%"
              left="5%"
              right="5%"
              borderRadius="sm"
              overflow="hidden"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <img
                src="/mobile/RackGuideImage.svg"
                alt=""
                aria-hidden="true"
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "contain",
                }}
              />
            </Box>
          </Box>

          <Text fontSize="sm" color="gray.600" lineHeight="tall">
            Coracle allows you to use your phone's camera to record a coral rack
            observation.
            <br />
            <br />
            Align the coral rack within the image guidelines, and ensure that
            the setting is not too bright, dark, or have too many reflections to
            capture the coral accurately. After you capture the observation,
            machine learning models will detect the number, size, and health of
            the fragments.
          </Text>

          <Flex gap={2} w="full">
            <Button
              flex="1"
              variant="outline"
              size="sm"
              onClick={() => fileInputRef.current?.click()}
            >
              Upload a file instead
            </Button>
            <Button
              flex="1"
              colorPalette="blue"
              size="sm"
              onClick={() => setModalDismissed(true)}
            >
              Let's go
            </Button>
          </Flex>

          <Checkbox.Root
            checked={photoGuideHidden}
            onCheckedChange={({ checked }) => setHidePhotoGuide(!!checked)}
            size="sm"
          >
            <Checkbox.HiddenInput />
            <Checkbox.Control />
            <Checkbox.Label fontSize="xs" color="gray.500">
              Don't show this again
            </Checkbox.Label>
          </Checkbox.Root>
        </Modal>
      )}

      {/* Hidden file input for upload */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        style={{ display: "none" }}
        onChange={handleFileChange}
      />
    </Box>
  );
}

export default LiveCamera;
